import { useCallback, useEffect, useRef, useState } from "react";
import config from "../api/config";

const SCRIPT_SELECTOR = 'script[data-recaptcha="v2"]';

export const useRecaptchaV2 = () => {
  const siteKey = config.recaptchaSiteKey;

  const [container, setContainer] = useState(null);
  const [token, setToken] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [recaptchaErrorOccurred, setRecaptchaErrorOccurred] = useState(false);
  const widgetIdRef = useRef(null);

  const containerRef = useCallback((node) => {
    setContainer(node);
  }, []);

  useEffect(() => {
    let isMounted = true;

    if (!siteKey) {
      setIsReady(false);
      setRecaptchaErrorOccurred(true);
      return () => {
        isMounted = false;
      };
    }

    setRecaptchaErrorOccurred(false);

    const handleReady = () => {
      if (!isMounted) return;
      setIsReady(true);
      setRecaptchaErrorOccurred(false);
    };

    const handleError = () => {
      if (!isMounted) return;
      setIsReady(false);
      setRecaptchaErrorOccurred(true);
    };

    const ensureReady = () => {
      if (window.grecaptcha?.ready) {
        window.grecaptcha.ready(handleReady);
      } else {
        handleError();
      }
    };

    const existingScript = document.querySelector(SCRIPT_SELECTOR);

    if (window.grecaptcha?.ready) {
      ensureReady();
      return () => {
        isMounted = false;
      };
    }

    if (existingScript) {
      existingScript.addEventListener("load", ensureReady);
      existingScript.addEventListener("error", handleError);
      return () => {
        isMounted = false;
        existingScript.removeEventListener("load", ensureReady);
        existingScript.removeEventListener("error", handleError);
      };
    }

    const script = document.createElement("script");
    script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.dataset.recaptcha = "v2";
    script.addEventListener("load", ensureReady);
    script.addEventListener("error", handleError);
    document.head.appendChild(script);

    return () => {
      isMounted = false;
      script.removeEventListener("load", ensureReady);
      script.removeEventListener("error", handleError);
    };
  }, [siteKey]);

  useEffect(() => {
    if (!siteKey || !isReady || !container || widgetIdRef.current !== null) {
      return;
    }
    if (!window.grecaptcha?.render) {
      setRecaptchaErrorOccurred(true);
      return;
    }

    try {
      const widgetId = window.grecaptcha.render(container, {
        sitekey: siteKey,
        callback: (value) => {
          setToken(value);
          setRecaptchaErrorOccurred(false);
        },
        "expired-callback": () => {
          setToken(null);
        },
        "error-callback": () => {
          setToken(null);
          setRecaptchaErrorOccurred(true);
        },
      });

      widgetIdRef.current = widgetId;
    } catch {
      setRecaptchaErrorOccurred(true);
    }
  }, [container, isReady, siteKey]);

  const reset = useCallback(() => {
    if (widgetIdRef.current !== null && window.grecaptcha?.reset) {
      window.grecaptcha.reset(widgetIdRef.current);
      setToken(null);
    }
  }, []);

  return {
    containerRef,
    token,
    reset,
    isReady,
    recaptchaErrorOccurred,
  };
};
